import { HttpErrorResponse } from "@angular/common/http";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewRef,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { Observable, Subscription, firstValueFrom, take } from "rxjs";
import { BaseComponent } from "src/app/@base/base.component";
import { ModalConfirmComponent } from "src/app/components/modal/modal-confirm/modal-confirm.component";
import { ModalLoadingComponent } from "src/app/components/modal/modal-loading/modal-loading.component";
import {
  LOCAL_STORAGE_KEY_ACCESS_POINT,
  LOCAL_STORAGE_KEY_PERMISSIONS,
  LOCAL_STORAGE_KEY_USER,
} from "src/app/const/localStorageKeys";
import { EnumPath } from "src/app/enum/path";
import { CurrentUser } from "src/app/models/user/user";
import { Auth, CheckDateExpire } from "../../models/auth/auth";
import { ModalSuccessComponent } from "src/app/components/modal/modal-success/modal-success.component";
import { WebAuthnService } from "src/app/services/web-authn.service";
import { PwaServiceService } from "src/app/services/pwa-service.service";
import { RecaptchaComponent } from "ng-recaptcha-2";
import { Comissioned } from "@models/comissioned";
import { KeyValueOptions } from "@models/settings/settings";
import { CustomAutocompleteComponent } from "@forms/custom-inputs/custom-autocomplete/custom-autocomplete.component";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent
  extends BaseComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("recaptcha") captchaElem: RecaptchaComponent;
  @ViewChild(CustomAutocompleteComponent) customAutoCompleteComponent: CustomAutocompleteComponent;
  public $subscriptionCaptcha: Subscription;
  public $subscriptionLoginGoogle: Subscription;

  public loginForm: FormGroup;

  public username: string = "";
  public loading: boolean = false;
  public hidePassword: boolean = true;
  public recaptchaResolved: boolean = false;
  public facebookLoginEnabled = false;
  public googleLoginEnabled = false;
  public modalLoading: MatDialogRef<ModalLoadingComponent>;
  public hasBiometric: boolean = this.webAuthN.hasBiometric();
  public showAccessPointSelection: boolean = false;
  public comissioners: Comissioned[];
  public filteredComissioners: KeyValueOptions[] = [];
  public selectedStructureType: string = "";
  public selectedAccessPointForm: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private webAuthN: WebAuthnService,
    public pwaService: PwaServiceService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  public detectar_mobile() {
    if (
      navigator.userAgent.match(/Android/i) ||
      navigator.userAgent.match(/webOS/i) ||
      navigator.userAgent.match(/iPhone/i) ||
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPod/i) ||
      navigator.userAgent.match(/BlackBerry/i) ||
      navigator.userAgent.match(/Windows Phone/i)
    ) {
      return true;
    } else {
      return false;
    }
  }

  ngOnInit(): void {
    if (this.webAuthN.hasBiometric()) {
      this.onSumitBio();
    }
    this.createForm();
  }

  ngAfterViewInit(): void {
    this.subscriptionChangeCaptcha();
  }

  ngOnDestroy(): void {
    this.unsubscriptionCaptcha();
    this.unsubscriptionLoginGoogle();
  }

  private createForm(): void {
    this.loginForm = this.formBuilder.group({
      username: ["", Validators.required],
      password: ["", Validators.required],
    });

    this.selectedAccessPointForm = this.formBuilder.group({
      selectedAccessPoint: new FormControl([], [Validators.required])
    });

    this.addDynamicEmailValidation();
  }

  public resetStructureForm() {
    this.selectedAccessPointForm.reset();
    this.customAutoCompleteComponent.resetFormValues();
  }

  private subscriptionChangeCaptcha(): void {
    this.$subscriptionCaptcha = this.captchaElem.resolved.subscribe((x) => {
      if (x === null) {
        this.recaptchaResolved = false;
      } else {
        this.recaptchaResolved = true;
      }
    });
  }

  private unsubscriptionCaptcha(): void {
    this.$subscriptionCaptcha?.unsubscribe();
  }
  private unsubscriptionLoginGoogle(): void {
    this.$subscriptionLoginGoogle?.unsubscribe();
  }

  public async requestGetPermissions(): Promise<void> {
    await firstValueFrom(this.authService.getCurrentUser())
      .then((resp: CurrentUser) => {
        this.setLocalStorageItem(
          LOCAL_STORAGE_KEY_PERMISSIONS,
          resp.permissions
        );
        this.setLocalStorageItem(LOCAL_STORAGE_KEY_USER, resp);

        return resp;
      })
      .then((resp: CurrentUser) => {
        this.setPermissions(resp.permissions);
        this.setUser(resp);
      });
  }

  public async login(
    username: string,
  ): Promise<void> {
    this.loginForm = this.formatFormValues(this.loginForm);

    let auth: Auth = {
      ...this.loginForm.value,
    };

    await this.loginBase(await this.authService.login(auth));
  }

  public async loginWebAuthN(): Promise<void> {
    this.loginForm = this.formatFormValues(this.loginForm);

    await this.loginBase(await this.webAuthN.authenticate());
  }

  private async loginBase(request: Observable<any>): Promise<void> {
    this.loading = true;
    await firstValueFrom(request)
      .then(async (res: any) => {
        if (res.hasOtherSession) {
          const confirmed = await firstValueFrom(
            this.openModalOtherSession().afterClosed()
          );

          if (!confirmed) {
            this.authService.newLogout().subscribe(() => {
              this.loading = false;
            });
            return;
          }
          await firstValueFrom(this.authService.invalidate());
        }

        if (this.detectar_mobile() && !this.webAuthN.hasBiometric()) {
          const response = await firstValueFrom(
            this.dialog
              .open(ModalConfirmComponent, {
                maxWidth: "600px",
                data: "Agora você pode configurar a sua biometria para acessar no próximo login. Deseja configurar agora?",
              })
              .afterClosed()
          );

          if (response) {
            try {
              const registerResponse = await firstValueFrom(
                this.webAuthN.register(this.loginForm.value)
              );
            } catch (error) {
              this.openDialogError(
                "Ocorreu um erro ao registrar, tente novamente no seu próximo acesso!",
                "Não foi possível entrar"
              );
            }
          }
        }

        if (this.loginForm.value.username && this.loginForm.value.password) {
          this.loginForm = this.formatFormValues(this.loginForm);

          let auth: Auth = {
            ...this.loginForm.value,
          };
          await this.getDaysToExpirePassword(auth);
        }

        await firstValueFrom(this.authService.createCache());
        await this.requestGetPermissions();
      })
      .then(() => {
        this.comissioners = this.getUser().comissioners;

        if (this.isComissionersListEmpty() || this.isSingleComissioner()) {
          this.navigateToHome();
          return;
        }

        this.showAccessPointSelection = true;
        this.selectedStructureType = this.determineStructureType();

        // Force Structure Form Biding
        this.changeDetectorRef.detectChanges();

        this.filterComissioners();
      })
      .catch(async (error: HttpErrorResponse) => {
        if (error.error.message === "Password expired") {
          this.authService.document = this.loginForm.value.username;
          await this.authService
            .GetUserEmail(this.loginForm.value.username)
            .pipe(take(1)) //this will limit the observable to only one value
            .subscribe((res: any) => {
              this.router.navigate(
                [EnumPath.EXPIRED_PASSWORD_MESSAGE, "false"],
                { state: { email: res.email } }
              );
            });
        } else if (error.error.message === "Password expired intern") {
          this.authService.document = this.loginForm.value.username;
          this.router.navigate([EnumPath.EXPIRED_PASSWORD_MESSAGE, "true"]);
        } else if (error.status !== 500) {
          this.openDialogError(error, "Não foi possível entrar");
        }
      })
      .finally(() => {
        this.closeModalLoading();
        this.loading = false;

        if (this.captchaElem?.reset) {
          this.captchaElem.reset();
        }
        this.recaptchaResolved = false;
      });
  }

  public onSubmit(): void {
    if (!this.loginForm.valid || this.loading || !this.recaptchaResolved) return;
    this.login(this.loginForm.get("username").value);
  }

  public async onSumitBio() {
    await this.loginWebAuthN();
  }

  public getDaysToExpirePassword(auth: Auth): void {
    firstValueFrom(this.authService.getDaysToExpirePassword(auth)).then(
      (resp: CheckDateExpire) => this.showModalDaysToExpirePassword(resp)
    );
  }

  public showModalDaysToExpirePassword(checkDateExpire: CheckDateExpire): void {
    if (checkDateExpire.shouldShowMessage) {
      firstValueFrom(
        this.dialog
          .open(ModalSuccessComponent, {
            maxWidth: "900px",
            data: {
              message:
                "Sua senha vence em " +
                checkDateExpire.daysToExpire +
                " dias.\r\n Após este prazo, a troca de senha será obrigatória para entrar no sistema.",
              title: "Aviso de Expiração de Senha",
            },
          })
          .afterClosed()
      );
    }
  }



  private closeModalLoading(): void {
    if (this.modalLoading !== undefined && this.modalLoading !== null)
      this.modalLoading.close();
  }

  private openModalOtherSession(): MatDialogRef<ModalConfirmComponent> {
    return this.dialog.open(ModalConfirmComponent, {
      maxWidth: "900px",
      data: {
        message:
          "Detectamos que existe um outro acesso ativo no momento. \r\nAo confirmar sua entrada, os acessos anteriores serão encerrados.\r\nDeseja prosseguir?",
        title: "Confirmação de Login",
      },
    });
  }

  private addDynamicEmailValidation(): void {
    const usernameControl = this.loginForm.get("username");

    if (usernameControl) {
      usernameControl.valueChanges.subscribe((value: string) => {
        if (value.includes("@")) {
          usernameControl.setValidators([Validators.required, Validators.email]);
        } else {
          usernameControl.setValidators([Validators.required]);
        }

        usernameControl.updateValueAndValidity({ emitEvent: false });
      });
    }
  }

  onSubmitAccessPoint() {
    const selectedAccessPoint = this.comissioners.find(c => c.id == this.selectedAccessPointForm.get('selectedAccessPoint').value[0]);
    this.loading = true;
    this.setLocalStorageItem(LOCAL_STORAGE_KEY_ACCESS_POINT, selectedAccessPoint)
    this.navigateToHome()
  }

  private navigateToHome() {
    this.router.navigate([EnumPath.HOME]);
  }

  private isComissionersListEmpty(): boolean {
    return this.comissioners.length === 0;
  }

  private isSingleComissioner(): boolean {
    const isSingle = this.comissioners.length === 1;

    if (isSingle) {
      this.setLocalStorageItem(LOCAL_STORAGE_KEY_ACCESS_POINT, this.comissioners[0]);
    }

    return isSingle;
  }

  private determineStructureType(): string {
    const hasEconomicGroup = this.comissioners.some(x => x.isEconomicGroup);
    const hasNonEconomicGroup = this.comissioners.some(x => !x.isEconomicGroup);

    //caso tiver duas estruturas, deixa Ponto de Venda como padrão selecionado
    if (hasEconomicGroup && hasNonEconomicGroup) {
      return "pointOfSale";
    }

    //caso contrário rádio button não será exibida
    return null;
  }

  filterComissioners(): void {
    const toKeyValue = (comissioned: Comissioned) => ({ key: comissioned.identifierCode + ' - ' + comissioned.description, value: comissioned.id })

    if (this.selectedStructureType === 'economicGroup') {
      this.filteredComissioners = this.comissioners.filter(user => user.isEconomicGroup).map(toKeyValue);
    } else if (this.selectedStructureType === 'pointOfSale') {
      this.filteredComissioners = this.comissioners.filter(user => !user.isEconomicGroup).map(toKeyValue);
    } else {
      this.filteredComissioners = [...this.comissioners].map(toKeyValue);
    }

    // Resetar o ponto de acesso selecionado para evitar inconsistências
    this.resetStructureForm();
  }

  private formatFormValues(form: FormGroup<any>): FormGroup<any> {
    let username: string = form.get('username').value;
    username = username.trim();
    username = this.formatIfCpfCnpj(username);

    let password: string = form.get('password').value;
    password = password.trim();

    form.patchValue({
      username,
      password
    })

    return form;
  }

  private formatIfCpfCnpj(cpfOrCnpj: string): string {
    const isCnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$|^\d{14}$/;
    const isCpfRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/;

    if (isCpfRegex.test(cpfOrCnpj) || isCnpjRegex.test(cpfOrCnpj)) {
      const rawDocument = cpfOrCnpj.replaceAll(".", "")
        .replaceAll("-", "")
        .replaceAll("/", "");

      return rawDocument;
    }


    return cpfOrCnpj;
  }
}
